import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'

import { getUploader } from '@/modules/file/uploader'
import DropZoneUploader from '@/modules/base/components/file-uploader/drop-zone-uploader/drop-zone-uploader'
import { Title } from '@/modules/base/components/texts'

import UploadsIcon from '@assets/images/icons/picture_upload.svg'
import DeleteIcon from '@assets/images/icons/trashcan.svg'

import { toast } from '@/modules/base/components/toast/toastConfig'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

const MAX_FILESIZE_BYTES = 5000000
const MIN_IMAGE_HEIGHT = 380
const MIN_IMAGE_WIDTH = 380

const DropZoneInner = ({ appIcon, allowedExtensions }) => {
  const classes = useStyles()

  const icon = lang.marketplace.form.general.sections.icon

  return (
    <Fragment>
      <div className={classes.iconUploadInner}>
        {appIcon && <img src={appIcon} />}
        {!appIcon && (
          <Fragment>
            <SVGIcon
              width="83.3px"
              svg={UploadsIcon}
              className={classes.uploadIcon}
            />
            <Title size="small">{icon.claim}</Title>
            <p>{icon.dependencies(allowedExtensions, MIN_IMAGE_HEIGHT, MIN_IMAGE_WIDTH, MAX_FILESIZE_BYTES)}</p>
          </Fragment>
        )}
      </div>
    </Fragment>
  )
}

const IconUpload = ({ allowedExtensions, appIcon, setNewIcon, disabled, onDelete }) => {
  const classes = useStyles()

  const onError = (id, name, errorReason, xhr) => {
    toast.showToast({ type: 'error', message: errorReason })
  }

  const onSubmitted = (id, name) => {
    const file = uploader.methods.getFile(id)
    const reader = new FileReader()
    reader.onabort = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onerror = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onload = () => {
      // Do whatever you want with the file contents
      const data = reader.result
      setNewIcon(file, data)
    }
    reader.readAsDataURL(file)
  }

  const uploader = getUploader({
    autoUpload: false,
    validation: {
      allowedExtensions,
      sizeLimit: MAX_FILESIZE_BYTES,
      image: {
        minHeight: MIN_IMAGE_HEIGHT,
        minWidth: MIN_IMAGE_HEIGHT,
      },
    },
    messages: {
      typeError: lang.fileUploaderTypesError.replace('%s', allowedExtensions.join(', ')),
      sizeError: lang.fileUploaderSizeError,
      minHeightImageError: lang.fileUploaderMinHeightImageError.replace('%s', MIN_IMAGE_HEIGHT),
      minWidthImageError: lang.fileUploaderMinWidthImageError.replace('%s', MIN_IMAGE_WIDTH),
    },
    request: {},
    callbacks: {
      onError,
      onSubmitted,
    },
  })

  return (
    <div className={classes.iconContainer}>
      <div className={classes.iconContent}>
        {!disabled && (
          <Fragment>
            <DropZoneUploader multiple={false} uploader={uploader}>
              <DropZoneInner
                appIcon={appIcon}
                allowedExtensions={allowedExtensions}
              />
            </DropZoneUploader>
            {appIcon && (
              <SVGIcon
                svg={DeleteIcon}
                width="10px"
                height="10px"
                onClick={onDelete}
                className={classes.removeIcon}
              />
            )}
          </Fragment>
        )}
        {disabled && (
          <DropZoneInner
            appIcon={appIcon}
            allowedExtensions={allowedExtensions}
          />
        )}
      </div>
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  iconContainer: {
    width: '100%',
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      paddingBottom: '100%',
    },
  },
  iconContent: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    '&:hover $removeIcon': {
      display: 'flex',
    },
  },
  uploadIcon: {
    fill: theme.primary,
  },
  iconUploadInner: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& img': {
      maxHeight: '100%',
    },
  },
  removeIcon: {
    width: 25,
    height: 25,
    position: 'absolute',
    right: 10,
    top: 10,
    background: theme.primary,
    display: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    '& path': {
      fill: '#fff',
    },
    borderRadius: '50%',
    cursor: 'pointer',
  },
}))

IconUpload.propTypes = {
  allowedExtensions: PropTypes.array,
  appIcon: PropTypes.string,
  setNewIcon: PropTypes.func,
  disabled: PropTypes.bool,
}

IconUpload.defaultProps = {
  allowedExtensions: ['jpg', 'png'],
  appIcon: null,
  setNewIcon: () => {},
  disabled: false,
}

export default IconUpload
